import * as THREE from 'three';

/**
 * Непрозрачный PBR-материал по идентификатору из каталога (ADR-0011).
 * Базовый уровень — приближённые цвета по группам; при загрузке контента
 * подключается провайдер с реальными текстурами (см. MaterialLibrary).
 */
function paramsFor(materialId: string | undefined): {
  color: THREE.ColorRepresentation;
  metalness: number;
  roughness: number;
} {
  if (!materialId) return { color: 0xd8d4cc, metalness: 0, roughness: 0.9 };
  const id = materialId.trim();
  if (id.startsWith('#')) return { color: id, metalness: 0.6, roughness: 0.5 }; // RAL
  const up = id.toUpperCase();
  if (up.startsWith('AL')) {
    const dark = up.startsWith('AL20') || up.startsWith('AL21');
    return { color: dark ? 0x2b2b2b : 0xb8b8bc, metalness: 0.9, roughness: 0.35 }; // алюминий
  }
  if (up.startsWith('WOOD') || up.startsWith('BW')) {
    return { color: 0x9c6b3f, metalness: 0, roughness: 0.8 }; // дерево
  }
  return { color: 0xeae6dd, metalness: 0, roughness: 0.85 }; // washi (тёплый белый)
}

const cache = new Map<string, THREE.MeshStandardMaterial>();

/** Материал только по цвету группы (без текстур) — базовый уровень/фолбэк. */
export function basicMaterial(materialId: string | undefined): THREE.MeshStandardMaterial {
  const key = materialId ?? '__default__';
  const cached = cache.get(key);
  if (cached) return cached;
  const { color, metalness, roughness } = paramsFor(materialId);
  const mat = new THREE.MeshStandardMaterial({ color, metalness, roughness });
  cache.set(key, mat);
  return mat;
}

/** Провайдер материалов (напр. с реальными текстурами), подменяющий базовый. */
export type MaterialProvider = (materialId: string | undefined) => THREE.Material;

let provider: MaterialProvider | null = null;

/** Установить провайдер материалов (или сбросить в null → базовые цвета). */
export function setMaterialProvider(p: MaterialProvider | null): void {
  provider = p;
}

/** Материал по идентификатору: через провайдер, иначе — базовый цвет. */
export function resolveMaterial(materialId: string | undefined): THREE.Material {
  return provider ? provider(materialId) : basicMaterial(materialId);
}
